/**
 * Typesetting for the numbers on a finding card — and nothing else.
 *
 * The engine hands over `computed` (parameter, value, operator, limit) so that
 * no consumer has to read a number back out of a German message string. This
 * module lays those values out; it never recomputes, never rounds and never
 * decides anything.
 *
 * Two small mappings live here:
 *
 *  · the operator glyph — `<=` is typeset as `≤`. Same operator, printed the
 *    way an architect writes it. The raw operator stays in a data attribute.
 *  · the unit — read off the engine's OWN parameter naming convention
 *    (`baumasse_hauptbau_m3` → m³, `gebaeudelaenge_m` → m,
 *    `abgrabung_anteil_prozent` → %). A parameter whose name carries no unit
 *    suffix gets NO unit; a unit is never invented, and a non-numeric value
 *    never gets one either.
 */

const OPERATOR_GLYPHS: Readonly<Record<string, string>> = {
  '<=': '≤',
  '>=': '≥',
  '<': '<',
  '>': '>',
  '==': '=',
  '=': '=',
  '!=': '≠',
}

/** `<=` → `≤`. An operator this table has never seen is passed through as-is. */
export function operatorGlyph(operator: string | null | undefined): string {
  if (!operator) return ''
  return OPERATOR_GLYPHS[operator] ?? operator
}

/** Suffix → unit. Longest suffix first, so `_m3` never matches as `_m`. */
const UNIT_SUFFIXES: readonly (readonly [string, string])[] = [
  ['_prozent', '%'],
  ['_m3', 'm³'],
  ['_m2', 'm²'],
  ['_m', 'm'],
]

/**
 * The unit of a parameter, from its name. `null` when the name does not carry
 * one — `parkplaetze_nachgewiesen` and `gueteklasse_faktor` are plain numbers
 * and stay plain numbers.
 */
export function unitFor(parameter: string | null | undefined): string | null {
  if (!parameter) return null
  const name = parameter.toLowerCase()
  for (const [suffix, unit] of UNIT_SUFFIXES) {
    if (name.endsWith(suffix)) return unit
  }
  return null
}

/** A unit belongs on a number, never on a boolean or a label. */
export function unitForValue(
  parameter: string | null | undefined,
  value: unknown,
): string | null {
  if (typeof value !== 'number') return null
  return unitFor(parameter)
}
